import _mapInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/map';
import _reduceInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/reduce';
import * as React from 'react';
import { ThemeProvider as ThemeProvider$1, createGlobalStyle } from 'styled-components';
import { jsx } from 'react/jsx-runtime';

let _ = t => t,
  _t;
const ThemeContext = /*#__PURE__*/React.createContext(null); // eslint-disable-line @typescript-eslint/naming-convention

/**
 * Provides a Bud UI theme to the components nested inside of it.
 * The theme provider component must be placed higher up than any other Bud UI components.
 *
 * @param props - Provider props including the theme to provide to components
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { bud } from '@thisisbud/bui-themes'
 * import { ThemeProvider } from '@thisisbud/bui-react-theme'
 *
 * export default function App(props) {
 *   return (
 *     <ThemeProvider theme={bud}>
 *       {props.children}
 *     </ThemeProvider>
 *   )
 * }
 * ```
 */
function ThemeProvider(props) {
  const {
    children,
    theme
  } = props;
  const {
    primary
  } = theme;
  return /*#__PURE__*/jsx(ThemeContext.Provider, {
    value: theme,
    children: /*#__PURE__*/jsx(ThemeProvider$1, {
      theme: primary,
      children: children
    })
  });
}
ThemeProvider.displayName = 'ThemeProvider';
function getFontStyles(props) {
  const {
    theme: {
      fonts = []
    }
  } = props;
  return _mapInstanceProperty(fonts).call(fonts, function (font) {
    const {
      display = 'fallback',
      name,
      styles
    } = font;
    return _mapInstanceProperty(styles).call(styles, function (style) {
      const {
        src,
        stretch,
        type,
        weight
      } = style;
      return `@font-face {
  font-display: ${display};
  font-family: "${name}";${typeof stretch !== 'undefined' ? `\n  font-stretch: ${stretch};` : ''}${typeof type !== 'undefined' ? `\n  font-style: ${type};` : ''}${typeof weight !== 'undefined' ? `\n  font-weight: ${weight};` : ''}
  src: ${_reduceInstanceProperty(src).call(src, function (acc, fontSrc) {
        acc.push(`url("${fontSrc.url}") format("${fontSrc.format}")`);
        return acc;
      }, []).join(', ')};
}`;
    }).join('\n');
  }).join('\n');
}

// eslint-disable-next-line @typescript-eslint/naming-convention
const GlobalStyleSheet = /*#__PURE__*/createGlobalStyle(_t || (_t = _`
${0}
body {
  -moz-osx-font-smoothing: grayscale;
  -webkit-font-smoothing: antialiased;
  font-smooth: always;
}
`), getFontStyles);

/**
 * Provides a global stylesheet for the Bud UI theme in use.
 * There should only be a single `GlobalStyle` component per application and it should be a direct child to the `ThemeProvider` component.
 *
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { GlobalStyle, ThemeProvider } from '@thisisbud/bui-react-theme'
 *
 * export default function App(props) {
 *   return (
 *     <ThemeProvider theme={bud}>
 *       <GlobalStyle/>
 *       {props.children}
 *     </ThemeProvider>
 *   )
 * }
 * ```
 */
function GlobalStyle() {
  // eslint-disable-line react/no-multi-comp
  const theme = React.useContext(ThemeContext);
  return /*#__PURE__*/jsx(GlobalStyleSheet, {
    theme: theme.global
  });
}
GlobalStyle.displayName = 'GlobalStyle';

/**
 * Switches the Bud UI theme currently in use from the primary scheme to the secondary scheme and vice versa depending on the active scheme.
 *
 * @param props - Props including children
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { ThemeSwitcher } from '@thisisbud/bui-react-theme'
 *
 * export default function Foobar(props) {
 *   return (
 *     <ThemeSwitcher>
 *       {props.children}
 *     </ThemeSwitcher>
 *   )
 * }
 * ```
 */
function ThemeSwitcher(
// eslint-disable-line react/no-multi-comp
props) {
  const theme = React.useContext(ThemeContext);
  const switchedTheme = React.useMemo(function () {
    return {
      global: theme.global,
      meta: theme.meta,
      primary: theme.secondary,
      secondary: theme.primary
    };
  }, [theme]);
  const {
    children
  } = props;
  return /*#__PURE__*/jsx(ThemeContext.Provider, {
    value: switchedTheme,
    children: /*#__PURE__*/jsx(ThemeProvider$1, {
      theme: switchedTheme.primary,
      children: children
    })
  });
}
ThemeSwitcher.displayName = 'ThemeSwitcher';

export { GlobalStyle, ThemeProvider, ThemeSwitcher };
